import { useState, useRef, useEffect, useId, useLayoutEffect, useMemo } from 'react';
import type { Session, SSEEvent } from '../api/types.js';
import { useAuth } from '../auth/index.js';
import { useSendPrompt, useRespondToQuestion, useInterruptSession, useRestoreSession, useCommands, useWorkspaceFolder } from '../hooks/use-session-actions.js';
import { applyCompletion, completionAt, latestSuggestion, matchCommands } from '../utils/prompt-completion.js';
import { PromptSuggestions, type Suggestion } from './prompt-suggestions.js';
import { ConfirmDialog } from './confirm-dialog.js';
import { Button } from './button.js';
import { GiveSecret } from './give-secret.js';

interface SessionControlsProps {
  session: Session;
  events?: SSEEvent[];
  serverRunning?: boolean;
}

function PermissionBanner({ session, events }: { session: Session; events?: SSEEvent[] }) {
  const respond = useRespondToQuestion(session.serverId, session.id);
  const [other, setOther] = useState('');

  if (session.status !== 'waiting_for_input') return null;

  // Find the last question event
  const questionEvent = events?.slice().reverse().find((e) => {
    const type = String(e.data.type ?? e.event);
    return type === 'question' || type === 'waiting';
  });

  const questionText = questionEvent
    ? String(questionEvent.data.content ?? questionEvent.data.question ?? questionEvent.data.message ?? 'Permission required')
    : 'Permission required';

  const promptId = questionEvent?.data.id ? String(questionEvent.data.id) : '';

  // AskUserQuestion carries its own choices; a permission prompt does not and is answered allow/deny.
  const options = Array.isArray(questionEvent?.data.options) ? (questionEvent!.data.options as unknown[]).map(String).filter(Boolean) : [];
  const isChoice = options.length > 0;

  const submitOther = (e: React.FormEvent) => {
    e.preventDefault();
    const text = other.trim();
    if (!text) return;
    respond.mutate({ promptId, response: text });
    setOther('');
  };

  // A question is a normal part of the conversation and takes the brand tone; amber stays for
  // permission prompts, where the operator is asked to authorise something.
  const tone = isChoice ? 'var(--color-brand)' : 'var(--color-warn)';
  const toneDim = isChoice ? 'var(--color-brand-dim)' : 'var(--color-warn-dim)';

  return (
    <div className="rounded-lg border p-4" style={{ borderColor: tone, backgroundColor: toneDim }} data-testid="permission-banner" data-kind={isChoice ? 'question' : 'permission'}>
      <div className="flex items-start gap-3">
        <svg className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: tone }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
          {isChoice
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />}
        </svg>
        <div className="flex-1">
          <p className="text-sm font-medium" style={{ color: tone }}>{isChoice ? 'The agent is asking' : 'Permission Required'}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm" style={{ color: 'var(--color-text-2)' }}>{questionText}</p>
          {isChoice ? (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center" data-testid="question-options">
              {options.map((option, i) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => respond.mutate({ promptId, response: option })}
                  disabled={respond.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                  style={i === 0
                    ? { backgroundColor: 'var(--color-brand)', borderColor: 'var(--color-brand)', color: '#fff' }
                    : { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-2)', color: 'var(--color-text)' }}
                >
                  {respond.isPending && <MiniSpinner />}
                  {option}
                </button>
              ))}
              <form onSubmit={submitOther} className="flex items-center gap-1.5 sm:ml-auto">
                <input
                  type="text"
                  value={other}
                  onChange={(e) => setOther(e.target.value)}
                  placeholder="Other answer…"
                  aria-label="Other answer"
                  disabled={respond.isPending}
                  className="rounded-md border px-2 py-1.5 text-xs"
                  style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-2)', color: 'var(--color-text)', minWidth: '12rem' }}
                />
                <button
                  type="submit"
                  disabled={respond.isPending || other.trim() === ''}
                  className="rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                  style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border-2)', color: 'var(--color-text)' }}
                >
                  Answer
                </button>
              </form>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => respond.mutate({ promptId, response: 'allow' })}
                disabled={respond.isPending}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-success)' }}
              >
                {respond.isPending && <MiniSpinner />}
                Allow
              </button>
              <button
                type="button"
                onClick={() => respond.mutate({ promptId, response: 'deny' })}
                disabled={respond.isPending}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-error)' }}
              >
                {respond.isPending && <MiniSpinner />}
                Deny
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PromptInput({ session, suggestion }: { session: Session; suggestion: { id: number; text: string } | null }) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaret = useRef<number | null>(null);
  // A touch screen: no Tab, no hardware Enter to send, no focus on open
  const touch = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const sendPrompt = useSendPrompt(session.serverId, session.id);

  // Completion as in Claude Code: `/` commands and skills at the start, `@` files and folders anywhere
  const listId = useId();
  const [caret, setCaret] = useState(0);
  // The active row, for the token it was moved on: a new token starts at the first row
  const [moved, setMoved] = useState<{ token: string | null; index: number }>({ token: null, index: 0 });
  const [closedAt, setClosedAt] = useState<string | null>(null);
  const completion = useMemo(() => completionAt(message, caret), [message, caret]);
  const commands = useCommands(session.serverId, completion?.kind === 'command');
  const folder = useWorkspaceFolder(session.serverId, completion?.kind === 'file' ? completion.dir : '', completion?.kind === 'file');
  const suggestions: Suggestion[] = useMemo(() => {
    if (!completion) return [];
    if (completion.kind === 'command') return matchCommands(commands.data ?? [], completion.query).map((c) => ({ value: c, label: c }));
    const name = completion.name.toLowerCase();
    return (folder.data ?? [])
      .filter((e) => e.name.toLowerCase().startsWith(name) && (name.startsWith('.') || !e.name.startsWith('.')))
      .sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type === 'directory' ? -1 : 1))
      .slice(0, 8)
      .map((e) => ({ value: `${completion.dir ? `${completion.dir}/` : ''}${e.name}`, label: e.name, folder: e.type === 'directory' }));
  }, [completion, commands.data, folder.data]);
  // Escape closes the list until the token changes
  const token = completion ? message.slice(completion.start, caret) : null;
  const open = suggestions.length > 0 && token !== closedAt;
  const active = moved.token === token ? moved.index : 0;
  const setActive = (index: number) => setMoved({ token, index });

  function pick(s: Suggestion) {
    if (!completion) return;
    const value = completion.kind === 'file' ? `${s.value}${s.folder ? '/' : ' '}` : s.value;
    const next = applyCompletion(message, caret, completion, value);
    pendingCaret.current = next.caret;
    setMessage(next.text);
    setCaret(next.caret);
  }

  // The caret after an insertion, set as soon as the new text is in the field: a key typed right after lands there
  useLayoutEffect(() => {
    const at = pendingCaret.current;
    if (at === null || !textareaRef.current) return;
    pendingCaret.current = null;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(at, at);
  }, [message]);

  const isTerminated = session.status === 'terminated';
  // While the session works, a message is queued: the session takes it between two steps (#945)
  const canSubmit = session.status !== 'terminated' && session.status !== 'waiting_for_input';

  // Ready to type when the session opens, with a keyboard; never on a phone, where it would pop the keyboard up
  useEffect(() => {
    if (!touch && canSubmit) textareaRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [message]);

  function handleSubmit() {
    const trimmed = message.trim();
    if (!trimmed || !canSubmit || sendPrompt.isPending) return;
    sendPrompt.mutate(trimmed, {
      onSuccess: () => setMessage(''),
    });
  }

  // Claude Code's proposed next message, shown in the empty field: Tab writes it, Escape sets it aside (and gives Tab
  // back to the page). A phone has no Tab: a Use button instead, and no hint about a key it lacks (`touch`)
  // Set aside by its event, not its text: the next turn may propose the same words again
  const [setAside, setSetAside] = useState<number | null>(null);
  const offered = suggestion && suggestion.id !== setAside && !message && session.status === 'idle' ? suggestion.text : null;
  function takeSuggestion() {
    if (!offered) return;
    pendingCaret.current = offered.length;
    setMessage(offered);
    setCaret(offered.length);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (offered && e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      takeSuggestion();
      return;
    }
    if (offered && e.key === 'Escape') {
      e.preventDefault();
      setSetAside(suggestion!.id);
      return;
    }
    if (open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((active + (e.key === 'ArrowDown' ? 1 : suggestions.length - 1)) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey)) {
        e.preventDefault();
        pick(suggestions[Math.min(active, suggestions.length - 1)]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setClosedAt(token);
        return;
      }
    }
    // As in Claude Code: Enter sends, Shift+Enter is a new line. On a touch screen Enter stays a new line (the Send
    // button sends); Ctrl/Cmd+Enter sends everywhere. A key that ends an IME composition is the composition's
    if (e.key === 'Enter' && !e.nativeEvent.isComposing && (e.ctrlKey || e.metaKey || (!touch && !e.shiftKey))) {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <div className="relative flex items-end gap-2" data-testid="prompt-input">
      {open && completion && (
        <PromptSuggestions id={listId} completion={completion} items={suggestions} active={active} onPick={pick} onHover={setActive} />
      )}
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => {
          const at = e.target.selectionStart ?? e.target.value.length;
          setMessage(e.target.value);
          setCaret(at);
          // A list closed with Escape opens again once its token is gone
          if (!completionAt(e.target.value, at)) setClosedAt(null);
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${Math.min(active, suggestions.length - 1)}` : undefined}
        aria-label="Message to the session: / for commands and skills, @ for files"
        disabled={isTerminated}
        placeholder={
          offered ? (touch ? offered : `${offered}   (Tab, Escape to dismiss)`) : session.status === 'terminated'
            ? 'Session terminated'
            : session.status === 'processing'
              ? 'Add a message: the session takes it between two steps…'
              : session.status === 'waiting_for_input'
                ? 'Answer the question above first…'
                : touch ? 'Send a message...' : 'Send a message (Enter to send, Shift+Enter for a new line)'
        }
        rows={1}
        className="prompt-textarea flex-1 resize-none rounded-lg border px-3 py-2 text-sm disabled:opacity-50"
        style={{
          borderColor: 'var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text)',
          fontFamily: 'var(--font-body)',
        }}
      />
      {offered && touch && (
        <Button size="sm" variant="ghost" className="flex-shrink-0 max-sm:min-h-10" onClick={takeSuggestion} title={offered} aria-label={`Write the suggestion: ${offered}`}>Use</Button>
      )}
      <Button
        onClick={handleSubmit}
        disabled={!canSubmit || !message.trim() || sendPrompt.isPending}
        className="flex-shrink-0"
      >
        {sendPrompt.isPending ? 'Sending...' : 'Send'}
      </Button>
    </div>
  );
}

function InterruptButton({ session }: { session: Session }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const interrupt = useInterruptSession(session.serverId, session.id);

  if (session.status !== 'processing') return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setShowConfirm(true)}
        disabled={interrupt.isPending}
        className="rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        style={{ backgroundColor: 'var(--color-error)' }}
        data-testid="interrupt-button"
      >
        {interrupt.isPending ? 'Interrupting...' : 'Interrupt'}
      </button>
      <ConfirmDialog
        open={showConfirm}
        title="Interrupt session"
        message="This will send SIGINT to the agent. Messages it has not taken yet are lost. Proceed?"
        confirmLabel="Interrupt"
        onConfirm={() => {
          setShowConfirm(false);
          interrupt.mutate();
        }}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
}

function RestoreBanner({ session, serverRunning }: { session: Session; serverRunning: boolean }) {
  const restore = useRestoreSession(session.serverId, session.id);

  if (session.status !== 'terminated') return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border p-3" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }} data-testid="restore-banner">
      <span className="text-sm" style={{ color: 'var(--color-text-2)' }}>Session terminated</span>
      {serverRunning && (
        <>
          <Button
            onClick={() => restore.mutate()}
            disabled={restore.isPending}
          >
            {restore.isPending ? 'Restoring...' : 'Restore'}
          </Button>
          {restore.isError && (
            <span className="text-xs" style={{ color: 'var(--color-error)' }}>{restore.error.message}</span>
          )}
        </>
      )}
    </div>
  );
}

export function SessionControls({ session, events, serverRunning = true }: SessionControlsProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  if (!isAdmin) return null;

  return (
    <div className="space-y-3" data-testid="session-controls">
      <PermissionBanner session={session} events={events} />
      <RestoreBanner session={session} serverRunning={serverRunning} />
      {/* A credential for this session's task, given out of the conversation (ADR-043 section 2) */}
      {session.status !== 'terminated' && <GiveSecret serverId={session.serverId} sessionId={session.id} />}
      {session.status !== 'terminated' && <PromptInput session={session} suggestion={latestSuggestion(events)} />}
    </div>
  );
}

export { InterruptButton };

function MiniSpinner() {
  return (
    <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
